import React, { useState } from "react";
import { INK, MUTED, PAPER, W, M, axis, scale, fmt, plural, lowerFirst, sentenceList, clusterColor, Halo, Legend, Tooltip, Axes } from "./clusterKit";

// ---- clusters: one scatter, every case colored by its cluster, each cluster's center ringed ----
// Props (ChartData#clusters): variableNames [names], scores [[score per variable]], member [cluster index per case],
// clusters [{ name, n, center [per variable] }], xIndex, yIndex (the pair shown first), legendTitle, noun;
// range [lo, hi] (settings) runs both axes over a rating scale's ends, one tick per point.
// With three or more variables, buttons switch between pairs; the clusters were formed on all of them.
export default function ClustersChart({ variableNames, scores, member, clusters, xIndex = 0, yIndex = 1, range, legendTitle, noun = "participant" }) {
	const [pair, setPair] = useState([xIndex, yIndex]);
	const [hover, setHover] = useState(null);
	const [xi, yi] = pair;
	const pairs = variableNames.flatMap((_, a) => variableNames.map((__, b) => [a, b]).filter(([p, q]) => p < q));
	const pairLabel = ([a, b]) => `${variableNames[a]} × ${variableNames[b]}`;
	const height = 340, box = { l: 44, r: W - M.right, t: M.top + 16, b: height - 40 };
	const fixed = range && { min: range[0] - 0.25, max: range[1] + 0.25, ticks: Array.from({ length: range[1] - range[0] + 1 }, (_, i) => range[0] + i) };
	const axX = fixed || axis(scores.map((s) => s[xi])), axY = fixed || axis(scores.map((s) => s[yi]));
	const x = scale(axX, box.l, box.r), y = scale(axY, box.b, box.t);
	const xName = variableNames[xi], yName = variableNames[yi];
	const hidden = variableNames.filter((_, j) => j !== xi && j !== yi).map(lowerFirst);
	const button = (active) => `px-2.5 py-1 ${active ? "bg-gray-100 font-semibold text-gray-800" : "text-gray-600 hover:bg-gray-50"}`;
	const same = (p) => p[0] === xi && p[1] === yi;
	const scoreText = (v) => (Number.isInteger(v) ? String(v) : fmt(v));
	return (
		<figure>
			{pairs.length > 1 && (
				<div className="mb-2 inline-flex flex-wrap overflow-hidden rounded border border-gray-200 text-xs" role="group" aria-label="Show">
					{pairs.map((p, i) => (
						<button key={pairLabel(p)} type="button" className={`${button(same(p))}${i ? " border-l border-gray-200" : ""}`} aria-pressed={same(p)}
							onClick={() => { setHover(null); setPair(p); }}>
							{pairLabel(p)}
						</button>
					))}
				</div>
			)}
			<Legend title={legendTitle} items={[...clusters.map((c, i) => ({ label: c.name, color: clusterColor(i) })), { label: "Center: the cluster's average", kind: "ring" }]} />
			<svg viewBox={`0 0 ${W} ${height}`} className="mt-2 w-full" role="img"
				aria-label={`${xName} against ${yName} for ${plural(scores.length, noun)}, colored by the cluster each is in. ${clusters.map((c) => `${c.name}: ${plural(c.n, noun)}, center ${fmt(c.center[xi])} on ${lowerFirst(xName)} and ${fmt(c.center[yi])} on ${lowerFirst(yName)}`).join("; ")}.`}>
				<Axes axX={axX} axY={axY} x={x} y={y} box={box} xLabel={xName} yLabel={yName} />
				{scores.map((s, i) => {
					const c = member[i], dim = hover && hover.c !== c;
					return (
						<circle key={i} cx={x(s[xi])} cy={y(s[yi])} r="3.5" fill={clusterColor(c)} stroke={PAPER} strokeWidth="1" opacity={dim ? 0.15 : 0.8}
							onMouseEnter={() => setHover({ c, cx: x(s[xi]), lines: [`One ${noun}: ${clusters[c].name}`, ...variableNames.map((v, j) => `${v}: ${scoreText(s[j])}`)] })}
							onMouseLeave={() => setHover(null)} />
					);
				})}
				{clusters.map((c, i) => {
					const cx = x(c.center[xi]), cy = y(c.center[yi]), right = cx + 150 < box.r;
					return (
						<g key={c.name} onMouseEnter={() => setHover({ c: i, cx, lines: [`${c.name}: center of ${plural(c.n, noun)}`, ...variableNames.map((v, j) => `${v}: ${fmt(c.center[j])}`)] })}
							onMouseLeave={() => setHover(null)}>
							<circle cx={cx} cy={cy} r="7.5" fill={clusterColor(i)} stroke={INK} strokeWidth="2" />
							<Halo x={cx + (right ? 12 : -12)} y={cy - 4} fontSize="12" fill={INK} fontWeight="600" textAnchor={right ? "start" : "end"}>{c.name}</Halo>
							<Halo x={cx + (right ? 12 : -12)} y={cy + 10} fill={MUTED} textAnchor={right ? "start" : "end"}>{plural(c.n, noun)}</Halo>
						</g>
					);
				})}
				{hover && <Tooltip x={hover.cx} lines={hover.lines} />}
			</svg>
			<figcaption className="mt-1 text-xs text-gray-600">
				Each dot is one {noun}, colored by the cluster k-means put them in; each ringed dot is a cluster's center, its members' average.
				{hidden.length ? ` The clusters were formed on ${variableNames.length === 3 ? "all three" : `all ${variableNames.length}`} measures at once, so a dot that looks closer to another cluster's center here may be farther from it on ${sentenceList(hidden)}.` : ""}
			</figcaption>
		</figure>
	);
}
